import { Glue42Core } from "@glue42/core";
import { Glue42 } from "../../../../glue";
import { ConfigurationData, DetachTabOptions, Executor, FlashOptions, FlashTabOptions, JumpListSettingsInternal, JumpListUpdateSettings } from "../../types";
import { ShowFlydownData, ReformatedFlydownOptions, ReformatedFlydownZone, ReformatedPopupOptions, DialogResult, StreamEvent } from "./types";
import { CallbackRegistry, default as CallbackFactory, UnsubscribeFunction } from "callback-registry";
import shortid from "shortid";
import windowStore from "../../store";
import { isNullOrWhiteSpace, isUndefinedOrNull } from "../../../activity/helpers/util";
import { isEmpty } from "../helpers";

export class GDExecutor implements Executor {

    private readonly GroupMethodName = "T42.Group.Execute";
    private readonly WndMethodName = "T42.Wnd.Execute";

    private agm: Glue42Core.Interop.API;
    private agmTarget: Glue42.Interop.InstanceTarget;
    private _registry: CallbackRegistry = CallbackFactory();
    private _finished: Promise<void> = Promise.resolve();
    private _configuration: ConfigurationData;
    private unsubCallbacks: { [key: string]: ((success: () => void, error: () => void, prevent?: (args?: any) => void, args?: { newUrl: string }) => void)[] } = {};

    public get hostInstance() {
        return this.agmTarget as Glue42Core.Interop.Instance;
    }

    public get finished() {
        return this._finished;
    }

    public get configuration() {
        return this._configuration;
    }

    public init(agm: Glue42Core.AGM.API, instance: Glue42Core.AGM.Instance) {
        this.agm = agm;
        this.agmTarget = instance;
        this._registry.add("event", (data: StreamEvent) => {
            if (data.type === "Closed") {
                const keys = Object.keys(this.unsubCallbacks);
                keys.forEach((key) => {
                    const isSameWindow = key.startsWith(data.windowId);
                    if (isSameWindow) {
                        delete this.unsubCallbacks[key];
                    }
                });
            }
        });
    }

    public setConfiguration(config?: ConfigurationData) {
        this._configuration = config;
    }

    // refactor
    public handleEvent(data: StreamEvent) {
        this._registry.execute("event", data);
    }

    public async open(options: Glue42.Windows.WindowCreateOptions) {
        let finishedResolve: () => void;
        this._finished = new Promise((resolve) => {
            finishedResolve = resolve;
        });
        try {
            const result = await this.agm.invoke<{ id: string }>("T42.Wnd.Create", options, this.agmTarget);
            if (result.returned === undefined) {
                throw new Error("failed to execute T42.Wnd.Create - unknown reason");
            }

            const id = result.returned.id;
            const win = await windowStore.waitFor(id);
            if (!this.configuration || this.configuration.windowAvailableOnURLChanged) {
                setTimeout(() => {
                    if (win.API.windowType === "electron") {
                        // on normal windows urlCHanged has to be detected first in order for the window to be ready
                        // that causes the user callbacks to miss urlChanged since they are not registered yet
                        // here we 'replay' the urlChanged event
                        win.Events.handleUrlChanged(win.API.url);
                    }
                }, 0);
            }
            return win.API;
        } finally {
            finishedResolve();
        }
    }

    public async close(w: Glue42.Windows.GDWindow, options: Glue42.Windows.CloseOptions): Promise<Glue42.Windows.GDWindow | boolean> {
        const result = await this.execute("close", { windowId: w.id, options }, `Closed`);
        if (options) {
            return result.closed;
        }
        return w;
    }

    public async navigate(w: Glue42.Windows.GDWindow, newUrl: string, urlLoadOptions: Glue42.Windows.LoadURLOptions): Promise<Glue42.Windows.GDWindow> {
        await this.execute("navigate", { windowId: w.id, options: { url: newUrl, urlLoadOptions } }, "UrlChanged");
        return w;
    }
    // Calls the public methods of the window. No Call to GD is made
    public async setStyle(w: Glue42.Windows.GDWindow, style: Glue42.Windows.WindowSettings): Promise<Glue42.Windows.GDWindow> {
        const stylePromises: Promise<any>[] = [];
        const wait = (promise: Promise<any>) => stylePromises.push(promise);

        if (!isUndefinedOrNull(style.focus) && !w.isFocused) {
            wait(w.focus());
        }

        // Todo: only if !isRestoring (hopefully setVisible does this check)
        if (!isUndefinedOrNull(style.hidden)) {
            const toBeVisible = !style.hidden;
            wait(w.setVisible(toBeVisible));
        }

        if (!isUndefinedOrNull(style.onTop)) {
            wait(w.setOnTop(style.onTop));
        }

        if (!isNullOrWhiteSpace(style.tabTooltip) || !isNullOrWhiteSpace((style as any).tabToolTip)) {
            const toolTip = style.tabTooltip ?? (style as any).tabToolTip;
            wait(w.setTabTooltip(toolTip));
        }

        if (!isNullOrWhiteSpace(style.tabTitle)) {
            wait(this.execute("setTabTitle", { windowId: w.id, options: { tabTitle: style.tabTitle } }));
        }

        const constraints: Glue42.Windows.SizeConstraints = {
            minHeight: style.minHeight,
            minWidth: style.minWidth,
            maxHeight: style.maxHeight,
            maxWidth: style.maxWidth,
        };

        const hasConstraints = !isEmpty(constraints);
        if (hasConstraints) {
            wait(w.setSizeConstraints(constraints));
        }

        const buttons: Glue42.Windows.WindowButtons = {
            allowClose: style.allowClose,
            allowCollapse: style.allowCollapse,
            allowLockUnlock: style.allowLockUnlock,
            allowMaximize: style.allowMaximize,
            allowMinimize: style.allowMinimize
        };
        const hasButtons = !isEmpty(buttons);
        if (hasButtons) {
            wait(w.resetButtons(buttons));
        }

        await Promise.all(stylePromises);

        return w;
    }

    public async setSizeConstraints(w: Glue42.Windows.GDWindow, constraints: Glue42.Windows.SizeConstraints): Promise<Glue42.Windows.GDWindow> {
        await this.execute("setSizeConstraints", { windowId: w.id, options: constraints });
        return w;
    }

    public async getSizeConstraints(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.SizeConstraints> {
        const sizeConstraint = await this.execute("getSizeConstraints", { windowId: w.id });
        return sizeConstraint;
    }

    public async setTabTooltip(w: Glue42.Windows.GDWindow, tabTooltip: string): Promise<Glue42.Windows.GDWindow> {
        await this.execute("setTabTooltip", { windowId: w.id, options: { tabTooltip } });
        return w;
    }

    public async getTabTooltip(w: Glue42.Windows.GDWindow): Promise<string> {
        const result = await this.execute("getTabTooltip", { windowId: w.id });
        return result.tabTooltip;
    }

    public async resetButtons(w: Glue42.Windows.GDWindow, buttonsConfig: Glue42.Windows.WindowButtons): Promise<Glue42.Windows.GDWindow> {
        await this.execute("resetButtons", { windowId: w.id, options: buttonsConfig });
        return w;
    }

    public async getButtons(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.WindowButtons> {
        const buttons = await this.execute("getButtons", { windowId: w.id });
        return buttons;
    }

    public async setOnTop(w: Glue42.Windows.GDWindow, onTop: boolean): Promise<Glue42.Windows.GDWindow> {
        await this.execute("setOnTop", { windowId: w.id, options: { onTop } });
        return w;
    }

    public async setTitle(w: Glue42.Windows.GDWindow, newTitle: string): Promise<Glue42.Windows.GDWindow> {
        const options = {
            windowId: w.id,
            options: {
                title: newTitle
            }
        };
        await this.execute("setTitle", options, "TitleChanged");
        return w;
    }

    public async setSticky(w: Glue42.Windows.GDWindow, isSticky: boolean): Promise<Glue42.Windows.GDWindow> {
        const options = {
            windowId: w.id,
            options: {
                isSticky
            }
        };
        await this.execute("setSticky", options);
        return w;
    }

    public async moveResize(w: Glue42.Windows.GDWindow, bounds: Partial<Glue42.Windows.Bounds>): Promise<Glue42.Windows.GDWindow> {
        if (typeof window !== "undefined" && (window.glueDesktop as any).versionNum < 31200) {
            return new Promise(async (res, rej) => {
                const resolveImmediately = this.areBoundsEqual(bounds, w);
                let isDone = false;
                const done = () => {
                    if (isDone) {
                        return;
                    }
                    isDone = true;

                    if (unsubscribeBoundsChanged) {
                        unsubscribeBoundsChanged();
                        unsubscribeBoundsChanged = undefined;
                    }
                    res(w);
                    if (resolveTimeout) {
                        clearTimeout(resolveTimeout);
                        resolveTimeout = undefined;
                    }
                };
                let resolveTimeout: any;
                let unsubscribeBoundsChanged: () => void;
                if (!resolveImmediately) {
                    unsubscribeBoundsChanged = w.onBoundsChanged((win: Glue42.Windows.GDWindow) => {
                        if (!this.areBoundsEqual(bounds, win)) {
                            return;
                        }
                        done();
                    });
                }

                try {
                    // always execute moveResize (we might have old bounds and prevent calls that are actually valid)
                    await this.execute("moveResize", { windowId: w.id, options: { bounds } });
                } catch (error) {
                    rej(error);
                    return;
                }

                // if we're trying to resize with the bounds we already have
                if (resolveImmediately) {
                    done();
                    return;
                }

                // if no bounds change in the next 1s resolve
                resolveTimeout = setTimeout(() => {
                    done();
                }, 1000);
            });
        } else {
            await this.execute("moveResize", { windowId: w.id, options: { bounds } });
        }
        return w;
    }

    public async addFrameButton(w: Glue42.Windows.GDWindow, buttonInfo: Glue42.Windows.ButtonInfo): Promise<Glue42.Windows.GDWindow> {
        await this.execute("addButton", { windowId: w.id, options: buttonInfo }, "ButtonAdded");
        return w;
    }

    public async removeFrameButton(w: Glue42.Windows.GDWindow, buttonId: string): Promise<Glue42.Windows.GDWindow> {
        await this.execute("removeButton", { windowId: w.id, options: buttonId }, "ButtonRemoved");
        return w;
    }

    public async activate(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        let unFocusChanged: UnsubscribeFunction;
        let unClosed: UnsubscribeFunction;
        try {
            const done = new Promise<void>((resolve, reject) => {
                unFocusChanged = w.onFocusChanged(() => {
                    resolve();
                });
                unClosed = w.onClose(() => {
                    reject(new Error("Window was closed"));
                });

            });
            await Promise.all([this.execute("activate", { windowId: w.id }, "FocusChanged"), done]);
            return w;
        } finally {
            if (unFocusChanged) {
                unFocusChanged();
            }
            if (unClosed) {
                unClosed();
            }
        }
    }

    public async focus(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        let unFocusChanged: UnsubscribeFunction;
        let unClosed: UnsubscribeFunction;
        try {
            const done = new Promise<void>((resolve, reject) => {
                unFocusChanged = w.onFocusChanged(() => {
                    resolve();
                });
                unClosed = w.onClose(() => {
                    reject(new Error("Window was closed"));
                });

            });
            await Promise.all([this.execute("focus", { windowId: w.id }, "FocusChanged"), done]);
            return w;
        } finally {
            if (unFocusChanged) {
                unFocusChanged();
            }
            if (unClosed) {
                unClosed();
            }
        }
    }

    public async maximizeRestore(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("maximizeRestore", { windowId: w.id }, "StateChanged");
        return w;
    }

    public async maximize(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("maximize", { windowId: w.id }, "StateChanged");
        return w;
    }

    public async restore(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("restore", { windowId: w.id }, "StateChanged");
        return w;
    }

    public async minimize(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("minimize", { windowId: w.id }, "StateChanged");
        return w;
    }

    public async collapse(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("collapse", { windowId: w.id }, "StateChanged");
        return w;
    }

    public async expand(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("expand", { windowId: w.id }, "StateChanged");
        return w;
    }

    public async toggleCollapse(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("toggleCollapse", { windowId: w.id }, "StateChanged");
        return w;
    }

    public async snap(w: Glue42.Windows.GDWindow, targetWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.SnappingOptions): Promise<Glue42.Windows.GDWindow> {
        const args: { targetWindowId: string, autoAlign?: boolean, snappingEdge?: string | undefined } = {
            targetWindowId: targetWindow.id
        };

        args.snappingEdge = options.direction;
        args.autoAlign = options.autoAlign;

        await this.execute("snap", { windowId: w.id, options: args }, "CompositionChanged", `CompositionChanged-${targetWindow.id}`);
        return w;
    }

    public async attachTab(w: Glue42.Windows.GDWindow, sourceWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.AttachOptions): Promise<Glue42.Windows.GDWindow> {
        await this.execute("attachTab", {
            windowId: w.id,
            options: {
                index: options,
                sourceWindowId: sourceWindow.id,
                targetWindowId: w.id,
            }
        }, `WindowFrameAdded-${sourceWindow.id}`, `WindowFrameRemoved-${sourceWindow.id}`);
        return w;
    }

    public async detachTab(w: Glue42.Windows.GDWindow, options: DetachTabOptions): Promise<Glue42.Windows.GDWindow> {
        const eventKeys: string[] = ["WindowFrameRemoved", `WindowFrameAdded`];
        if (!isUndefinedOrNull(options?.relativeTo)) {
            eventKeys.push(`CompositionChanged`);
            eventKeys.push(`CompositionChanged-${options.relativeTo}`);
        } else {
            eventKeys.push("BoundsChanged");
        }
        await this.execute("detachTab", { windowId: w.id, options }, ...eventKeys);
        return w;
    }

    public async setVisible(w: Glue42.Windows.GDWindow, toBeVisible = true): Promise<Glue42.Windows.GDWindow> {
        let command: string;
        if (toBeVisible) {
            command = "show";
        } else {
            command = "hide";
        }

        await this.execute(command, { windowId: w.id }, "VisibilityChanged");
        return w;
    }

    public async center(w: Glue42.Windows.GDWindow, display?: Glue42.Displays.Display): Promise<Glue42.Windows.GDWindow> {
        await this.execute("center", { windowId: w.id, options: display });
        return w;
    }

    public async showLoader(w: Glue42.Windows.GDWindow, loader?: Glue42.Windows.Loader): Promise<Glue42.Windows.GDWindow> {
        await this.execute("showLoadingAnimation", { windowId: w.id, options: loader });
        return w;
    }

    public async hideLoader(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("hideLoadingAnimation", { windowId: w.id });
        return w;
    }

    public async updateContext(w: Glue42.Windows.GDWindow, context: any, replace: boolean): Promise<Glue42.Windows.GDWindow> {
        let un: UnsubscribeFunction;
        try {
            // as undefined values do not go through interop, setting a prop to undefined will lead to no change
            // set this to null so it actually removes the prop
            const contextWithoutUndefinedValues = this.swapUndefinedToNull(context);

            const done = new Promise<void>((resolve, reject) => {
                un = w.onContextUpdated(() => {
                    resolve();
                });
            });
            await Promise.all([this.execute("updateContext", {
                windowId: w.id, context: contextWithoutUndefinedValues, replace
            }), done]);
            return w;
        } finally {
            if (un) {
                un();
            }
        }
    }

    public async lock(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("lockUnlock", { windowId: w.id, options: { lock: true } }, "FrameIsLockedChanged");
        return w;
    }

    public async unlock(w: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("lockUnlock", { windowId: w.id, options: { lock: false } }, "FrameIsLockedChanged");
        return w;
    }

    public async getIcon(w: Glue42.Windows.GDWindow): Promise<string> {
        const result = await this.execute("getIcon", {
            windowId: w.id,
            options: {}
        });
        return result.icon;
    }

    public async setIcon(w: Glue42.Windows.GDWindow, base64Image: string): Promise<Glue42.Windows.GDWindow> {
        await this.execute("setIcon", {
            windowId: w.id,
            options: {
                dataURL: base64Image
            }
        });
        return w;
    }

    public async setFrameColor(w: Glue42.Windows.GDWindow, frameColor: string): Promise<Glue42.Windows.GDWindow> {
        await this.execute("setFrameColor", { windowId: w.id, options: { frameColor } }, "FrameColorChanged");
        return w;
    }

    public async setTabHeaderVisible(w: Glue42.Windows.GDWindow, toBeTabHeaderVisible: boolean): Promise<Glue42.Windows.GDWindow> {
        await this.execute("setTabHeaderVisible", {
            windowId: w.id,
            options: {
                toShow: toBeTabHeaderVisible
            }
        }, "TabHeaderVisibilityChanged");
        return w;
    }

    public async showPopup(targetWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.PopupOptions): Promise<Glue42.Windows.GDWindow> {
        if (!options) {
            throw new Error("The options object is not valid!");
        }

        const optionsCopy = { ...options };

        if (!optionsCopy.targetLocation) {
            optionsCopy.targetLocation = "bottom" as Glue42.Windows.PopupTargetLocation;
        }

        const reformatedOptions: ReformatedPopupOptions & { targetId: string } = {
            ...optionsCopy,
            popupBounds: optionsCopy.size,
            targetId: targetWindow.id,
            popupId: optionsCopy.windowId
        };

        await this.execute("showPopupWindow", {
            windowId: targetWindow.id,
            options: reformatedOptions
        });

        return targetWindow;
    }

    public async createFlydown(windowId: string, options: Glue42.Windows.FlydownOptions) {
        if (!options) {
            throw new Error("The options object is not valid!");
        }

        const optionsCopy = { ...options };

        if (!optionsCopy.horizontalOffset) {
            optionsCopy.horizontalOffset = 0;
        }

        if (!optionsCopy.verticalOffset) {
            optionsCopy.verticalOffset = 0;
        }

        const fullOptions: Glue42.Windows.FlydownOptions & { targetId: string, flydownBounds: Glue42.Windows.FlydownOptions["size"] } = this.reformatFlydownOptions(windowId, optionsCopy);

        return this.execute("setFlydownArea", { windowId, options: fullOptions }).then(() => {
            const zoneIds = fullOptions.zones.map((z) => z.id);

            fullOptions.zones.forEach((z) => {
                let callback: (...args: any[]) => any = typeof (z.flydownSize) === "function" ?
                    z.flydownSize : () => z.flydownSize;

                if (options.size instanceof Function && z.flydownSize) {
                    callback = async (data: Glue42.Windows.ShowFlydownData, cancel: () => void) => {
                        let result;
                        // The options callback must be always invoked
                        // If the property in the zone is null it will be automatically
                        // assigned to the same callback from the code above reformatFlydownOptions
                        if (options.size instanceof Function) {
                            result = await options.size(data, cancel);
                        }

                        // If both are callbacks the zone has higher priority
                        // If they are the same only one should be invoked
                        if (z.flydownSize instanceof Function && z.flydownSize !== options.size) {
                            return await z.flydownSize(data, cancel) || result;
                        }

                        // The options callback overwrites the bounds object specified in the zone
                        return result || z.flydownSize;
                    };
                }
                this._registry.clearKey(`${fullOptions.targetId}_${z.id}`);
                this._registry.add(`${fullOptions.targetId}_${z.id}`, callback);
            });

            return {
                destroy: () => this.clearFlydownArea(fullOptions.targetId, zoneIds),
                options: optionsCopy
            };
        });
    }

    public async setModalState(windowId: string, isModal: boolean): Promise<void> {
        return this.execute("setModalState", { windowId, options: { isModal } });
    }

    public async handleFlydownBoundsRequested(targetId: string, data: ShowFlydownData) {
        const cancelCallback = () => data.cancel = true;

        const callbackData: Glue42.Windows.ShowFlydownData = {
            zoneId: data.flydownId,
            flydownWindowBounds: data.flydownWindowBounds,
            flydownWindowId: data.flydownWindowId,
        };

        const responses = await Promise.all(this._registry.execute(`${targetId}_${data.flydownId}`, callbackData, cancelCallback));

        if (responses.length === 1) {
            const defaultResponse = { height: 0, width: 0, top: 0, left: 0 };
            const response = typeof (responses[0]) === "object" && !Array.isArray(responses[0]) ? responses[0] : defaultResponse;
            const responseOptions: ShowFlydownData = { ...data, flydownWindowBounds: response };

            return responseOptions;
        }
    }

    public async handleOnEventRequested(callbackId: string, args?: { newUrl: string }) {
        const callbacks = this.unsubCallbacks[callbackId] ?? [];
        let prevented = false;
        const preventArgs: any[] = [];
        await Promise.all(callbacks.map((cb) => {
            return new Promise<void>((resolve, reject) => {
                cb(() => {
                    resolve();
                }, () => {
                    reject();
                }, (pArgs: any) => {
                    prevented = true;
                    preventArgs.push(pArgs);
                }, args);
            });
        }));
        return { prevented, preventArgs };
    }

    public async zoomIn(window: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("zoomIn", {
            windowId: window.id,
        });
        return window;
    }

    public async zoomOut(window: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("zoomOut", {
            windowId: window.id,
        });
        return window;
    }

    public async setZoomFactor(window: Glue42.Windows.GDWindow, zoomFactor: number): Promise<Glue42.Windows.GDWindow> {
        await this.execute("setZoomFactor", {
            windowId: window.id,
            options: {
                zoomFactor
            }
        });
        return window;
    }

    public async showDevTools(window: Glue42.Windows.GDWindow): Promise<Glue42.Windows.GDWindow> {
        await this.execute("showDevTools", {
            windowId: window.id,
        });

        return window;
    }

    public async capture(window: Glue42.Windows.GDWindow, options: Glue42.Windows.CaptureOptions): Promise<string> {
        const base64screenshot = (await this.execute(
            "captureScreenshot",
            { windowId: window.id, options: { ...options } }
        )).data;

        return base64screenshot;
    }

    public async captureGroup(windowIds: string[], options: Glue42.Windows.CaptureOptions): Promise<string> {
        const base64screenshot: string = (await this.execute(
            "captureGroupScreenshot",
            { windowId: windowIds[0], options: { groupWindowIds: windowIds, ...options } }
        )).data;

        return base64screenshot;
    }

    public async flash(resultWindow: Glue42.Windows.GDWindow, options: FlashOptions): Promise<Glue42.Windows.GDWindow> {
        await this.execute(
            "flash",
            { windowId: resultWindow.id, options: { ...options } }
        );

        return resultWindow;
    }

    public async flashTab(resultWindow: Glue42.Windows.GDWindow, options: FlashTabOptions): Promise<Glue42.Windows.GDWindow> {
        await this.execute(
            "flashTab",
            { windowId: resultWindow.id, options: { ...options } }
        );

        return resultWindow;
    }

    public async configure(windowId: string, options: Glue42.Windows.WindowsConfiguration): Promise<void> {
        return this.execute(
            "configure",
            { windowId, options: { ...options } }
        );
    }

    public async print(resultWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.PrintOptions): Promise<Glue42.Windows.GDWindow> {
        await this.execute(
            "print",
            { windowId: resultWindow.id, options: { ...options } }
        );
        return resultWindow;
    }

    public async printToPDF(resultWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.PrintToPDFOptions): Promise<string> {
        const filePath: string = (await this.execute(
            "printToPDF",
            { windowId: resultWindow.id, options: { ...options } }
        )).filePath;

        return filePath;
    }

    public async place(window: Glue42.Windows.GDWindow, options: Glue42.Windows.PlacementSettings): Promise<void> {
        const copy = { ...options } as { display: string | number | Glue42.Displays.Display };
        if (!options.display || options.display === "current") {
            copy.display = await window.getDisplay();
        }
        // Glue42.Displays.API works with zero based array of displays, which means that the first monitor has index 0
        // whereas GD works with non zero based array of displays, which means that the first monitor has index 1
        if (copy.display && typeof copy.display !== "string" && typeof copy.display !== "number") {
            copy.display = copy.display.index + 1;
        }

        return this.execute(
            "place",
            { windowId: window.id, options: { ...copy } }
        );
    }

    public async refresh(resultWindow: Glue42.Windows.GDWindow, ignoreCache: boolean): Promise<Glue42.Windows.GDWindow> {
        await this.execute("refresh", { windowId: resultWindow.id, options: { ignoreCache } });
        return resultWindow;
    }

    public async download(resultWindow: Glue42.Windows.GDWindow, url: string, options: Glue42.Windows.DownloadOptions & { enableDownloadBar?: boolean } = {}): Promise<Glue42.Windows.DownloadResult> {
        options.enableDownloadBar = !options.silent;
        const result: { fileSize: number, fullPath: string, success: boolean } = await this.execute("downloadURL", { windowId: resultWindow.id, options: { url, options } });
        return {
            url,
            path: result.fullPath,
            size: result.fileSize,
        };
    }

    public async configureWindow(resultWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.WindowConfiguration): Promise<Glue42.Windows.GDWindow> {
        await this.execute("configureWindow", { windowId: resultWindow.id, options });
        return resultWindow;
    }

    public async getWindowConfiguration(resultWindow: Glue42.Windows.GDWindow): Promise<Glue42.Windows.WindowConfiguration> {
        const config = await this.execute("getWindowConfiguration", { windowId: resultWindow.id });
        return config;
    }

    public async startDrag(resultWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.StartDragOptions): Promise<Glue42.Windows.GDWindow> {
        await this.execute("startDrag", { windowId: resultWindow.id, options });
        return resultWindow;
    }

    public showDialog<T>(resultWindow: Glue42.Windows.GDWindow, options: Glue42.Windows.DialogOptions): Promise<T> {
        return new Promise((res, rej) => {
            const token = shortid.generate();
            const un = this._registry.add("event", (args: { windowId: string, type: string, data: DialogResult<T>["data"] }) => {
                if (args.type === "DialogResult" && args.windowId === resultWindow.id && args.data.token === token) {
                    un();
                    const data = args.data;
                    if ("status" in data) {
                        if (data.status === "failed") {
                            rej(data.message);
                        } else if (data.status === "successful") {
                            res(data.result);
                        }
                    }
                }
            });
            this.execute("showDialog", { windowId: resultWindow.id, options: Object.assign({}, { ...options }, { token }) });
        });
    }

    /**
     * Invokes the container
     * @param command
     * @param options
     * @param {...string} eventKeys which events types to be awaited before resolving - used only < GD 3.12
     * @returns Invocation result
     */
    public async execute(command: string, options: any, ...eventKeys: string[]): Promise<any> {
        return this.executeCore(this.WndMethodName, command, options, ...eventKeys);
    }

    public async executeGroup(command: string, options: any, ...eventKeys: string[]): Promise<any> {
        return this.executeCore(this.GroupMethodName, command, options, ...eventKeys);
    }

    public async ungroup(w: Glue42.Windows.GDWindow, options: Glue42.Windows.UngroupOptions): Promise<Glue42.Windows.GDWindow> {
        const args = {
            windowId: w.id,
            options
        };
        await this.execute("ungroup", args);
        return w;
    }

    public async updateJumpList(windowId: string, options: JumpListUpdateSettings): Promise<void> {
        const args = {
            windowId,
            options
        };
        await this.execute("updateJumplist", args);
    }

    public async getJumpList(windowId: string): Promise<JumpListSettingsInternal> {
        const args = {
            windowId,
        };
        const result = await this.execute("getJumplist", args);
        return result;
    }

    public onClosing(callback: (success: () => void, error: () => void, prevent: (options?: Glue42.Windows.PreventClosingOptions) => void) => void, gdWindow: Glue42.Windows.GDWindow): () => void {
        const glue42gd = typeof window !== "undefined" && (window as any).glue42gd;
        if (glue42gd && gdWindow.windowType === "electron") {
            return glue42gd.addCloseHandler(callback, gdWindow.id);
        } else {
            return this.nonWindowHandlers(callback, gdWindow.id, "OnClosing");
        }
    }

    public onGroupClosing(callback: (success: () => void, error: () => void, prevent: () => void) => void, group: Glue42.Windows.Group): () => void {
        return this.nonWindowHandlersCore(group.id, "OnClosing", true, callback);
    }

    public onRefreshing(callback: (success: () => void, error: () => void, prevent: () => void) => void, gdWindow: Glue42.Windows.GDWindow): () => void {
        const glue42gd = typeof window !== "undefined" && (window as any).glue42gd;
        if (glue42gd && gdWindow.windowType === "electron") {
            return glue42gd.addRefreshHandler(callback, gdWindow.id);
        } else {
            return this.nonWindowHandlers(callback, gdWindow.id, "OnRefreshing");
        }
    }

    public onNavigating(callback: (success: () => void, error: () => void, prevent: () => void, args: { newUrl: string }) => void, gdWindow: Glue42.Windows.GDWindow): () => void {
        const glue42gd = typeof window !== "undefined" && (window as any).glue42gd;
        if (glue42gd && gdWindow.windowType === "electron") {
            return glue42gd.addWillNavigateHandler(callback, gdWindow.id);
        } else {
            return this.nonWindowHandlers(callback, gdWindow.id, "OnNavigating");
        }
    }

    public async goBack(resultWindow: Glue42.Windows.GDWindow): Promise<void> {
        await this.execute("goBack", { windowId: resultWindow.id });
    }

    public async goForward(resultWindow: Glue42.Windows.GDWindow): Promise<void> {
        await this.execute("goForward", { windowId: resultWindow.id });
    }

    public clearCallbacks(id: string): void {
        const keys = Object.keys(this.unsubCallbacks);
        keys.forEach((key) => {
            if (key.startsWith(id)) {
                delete this.unsubCallbacks[key];
            }
        });
    }

    private nonWindowHandlers(callback: (success: () => void, error: () => void, prevent?: (options?: Glue42.Windows.PreventClosingOptions) => void, args?: { newUrl: string }) => void, targetId: string, type: string): () => void {
        return this.nonWindowHandlersCore(targetId, type, false, callback);
    }

    private nonWindowHandlersCore(targetId: string, type: string, isGroup: boolean, callback: (success: () => void, error: () => void, prevent?: (options?: Glue42.Windows.PreventClosingOptions) => void, args?: { newUrl: string }) => void,): () => void {
        const id = `${targetId}-${type}`;

        const unsub = () => {
            if (this.unsubCallbacks[id]) {
                const callbacks = this.unsubCallbacks[id];
                this.unsubCallbacks[id] = callbacks.filter((cb) => cb !== callback);
                if (this.unsubCallbacks[id].length === 0) {
                    delete this.unsubCallbacks[id];
                }
            }
            const cbs = this.unsubCallbacks[id] ?? [];
            if (cbs.length === 0) {
                const options = {
                    unsubscribe: true
                };
                if (isGroup) {
                    this.executeGroup(type, {
                        groupId: targetId,
                        options
                    });
                } else {
                    this.execute(type, {
                        windowId: targetId,
                        options
                    });
                }
            }
        };

        if (this.unsubCallbacks[id]) {
            this.unsubCallbacks[id].push(callback);
            return unsub;
        } else {
            this.unsubCallbacks[id] = [callback];
        }
        if (isGroup) {
            this.executeGroup(type, { groupId: targetId });
        } else {
            this.execute(type, { windowId: targetId });
        }

        return unsub;
    }

    private reformatFlydownOptions(windowId: string, options: Glue42.Windows.FlydownOptions) {
        const assignGeneralIfUnassigned = (zone: Glue42.Windows.FlydownZone, prop: keyof Glue42.Windows.FlydownZone & keyof Glue42.Windows.FlydownOptions) => {
            if (options[prop] && (zone[prop] === undefined || zone[prop] === null)) {
                const valueFromOptions = options[prop];
                (zone[prop] as any) = valueFromOptions;
            }
        };

        const zones: ReformatedFlydownOptions["zones"] = options.zones.map((z) => {
            assignGeneralIfUnassigned(z, "windowId");
            assignGeneralIfUnassigned(z, "targetLocation");

            if (options.size && (z.flydownSize === undefined || z.flydownSize === null)) {
                z.flydownSize = options.size;
            }

            (z as ReformatedFlydownZone).flydownBounds = z.flydownSize as any;
            (z as ReformatedFlydownZone).flydownId = z.windowId;

            if (!z.targetLocation) {
                z.targetLocation = "bottom" as Glue42.Windows.PopupTargetLocation.Bottom;
            }

            return z;
        });

        return {
            ...options,
            zones,
            targetId: windowId,
            flydownBounds: options.size,
            flydownActiveArea: options.activeArea
        };
    }

    private clearFlydownArea(windowId: string, areaIds: string[]) {
        return this.execute("clearFlydownWindowArea", {
            windowId,
            options: {}
        }).then(() => {
            areaIds.forEach((id) => {
                this._registry.clearKey(`${windowId}_${id}`);
            });
        });
    }

    private executeWithoutToken(params: { windowId: string }, ...eventKeys: string[]) {
        const uns: UnsubscribeFunction[] = [];
        const executed = eventKeys?.filter((k) => !isUndefinedOrNull(k)).map((key) => {
            return new Promise<void>((r) => {
                const [type, windowId = params.windowId] = key.split("-");
                uns.push(this._registry.add("event", (data: { windowId: string, type: string }) => {
                    if (data.type === type && data.windowId === windowId) {
                        r();
                    }
                }));
            });
        });
        const action = new Promise<any>((resolve, reject) => {
            this.agm.invoke<any>("T42.Wnd.Execute", params, this.agmTarget)
                .then((i) => {
                    if (i.returned && i.returned.errorMsg) {
                        reject(i);
                    } else {
                        resolve(i.returned);
                    }
                })
                .catch((e) => reject(e));
        });
        return Promise.all([action, ...executed])
            .then((r) => {
                return r[0];
            })
            .finally(() => {
                uns.forEach((un) => un());
            });
    }

    private async executeCore(methodName: string, command: string, options: any, ...eventKeys: string[]): Promise<any> {
        const params = {
            ...options,
            command,
        };
        let finishedResolve: () => void;
        this._finished = new Promise((resolve) => {
            finishedResolve = resolve;
        });
        try {
            if (typeof window !== "undefined" && (window.glueDesktop as any).versionNum < 31200) {
                return await this.executeWithoutToken(params, ...eventKeys);
            } else {
                return await this.executeWithToken(methodName, params);
            }
        } finally {
            finishedResolve();
        }
    }

    private async executeWithToken(methodName: string, options: any) {
        let un: UnsubscribeFunction;
        try {
            const token = shortid.generate();
            const event = new Promise<void>((r) => {
                un = this._registry.add("event", (data: any) => {
                    if (data.token === token) {
                        r();
                    }
                });
            });
            const execute = new Promise<any>((resolve, reject) => {
                options.token = token;
                this.agm.invoke<any>(methodName, options, this.agmTarget)
                    .then((i) => {
                        if (i.returned && i.returned.errorMsg) {
                            reject(i);
                        } else {
                            resolve(i.returned);
                        }
                    })
                    .catch((i) => {
                        reject(i);
                    });
            });

            const result = await Promise.all([execute, event]);
            return result[0];
        } finally {
            if (un) {
                un();
            }
        }
    }

    private areBoundsEqual(requested: Partial<Glue42.Windows.Bounds>, w: Glue42.Windows.GDWindow) {
        const current = w.bounds;
        const settings = w.settings;
        let height = requested.height;
        let width = requested.width;
        if (requested.height < settings.minHeight) {
            height = settings.minHeight;
        }
        if (requested.height > settings.maxHeight) {
            height = settings.maxHeight;
        }
        if (requested.width < settings.minWidth) {
            width = settings.minWidth;
        }
        if (requested.width > settings.maxWidth) {
            width = settings.maxWidth;
        }

        const areHeightsEqual = height ? current.height === height : true;
        const areWidthsEqual = width ? current.width === width : true;
        const areLeftsEqual = requested.left ? current.left === requested.left : true;
        const areTopsEqual = requested.top ? current.top === requested.top : true;

        return areHeightsEqual && areWidthsEqual && areLeftsEqual && areTopsEqual;
    }

    private swapUndefinedToNull(context: any) {
        try {
            // swap top level undefined values to null-s
            const copy: any = {};
            for (const key of Object.keys(context)) {
                let value = context[key];
                if (typeof value === "undefined") {
                    value = null;
                }
                copy[key] = value;
            }
            return copy;
        } catch {
            return context;
        }
    }
}

export default new GDExecutor();
